import React, { useState } from 'react';
import confetti from 'canvas-confetti';
import {
  CheckCircle2,
  HelpCircle,
  Sparkles,
  Send,
  MessageCircle,
  ShieldAlert,
  Award,
  Zap,
} from 'lucide-react';

interface Question {
  id: number;
  text: string;
  category: string;
  impact: string;
}

const QUESTIONS: Question[] = [
  {
    id: 1,
    text: 'You receive monthly P&L, Balance Sheet, and Cash Flow statements',
    category: 'Core Visibility',
    impact: 'Crucial for basic solvency and historical tracking.',
  },
  {
    id: 2,
    text: 'You understand your gross margin and net margin by month',
    category: 'Margin Control',
    impact: 'Prevents profit erosion and pricing blindspots.',
  },
  {
    id: 3,
    text: 'You track budget vs actual performance every month',
    category: 'Accountability',
    impact: 'Ensures spending aligns with strategic company objectives.',
  },
  {
    id: 4,
    text: 'You know which product lines or services are most profitable',
    category: 'Unit Economics',
    impact: 'Directs capital to high-return offerings and eliminates loss-makers.',
  },
  {
    id: 5,
    text: 'You have a rolling 90-day cash flow forecast',
    category: 'Liquidity & Runway',
    impact: 'Protects the business against cash crunches and payroll delays.',
  },
  {
    id: 6,
    text: 'You know your breakeven point and monitor your contribution margin',
    category: 'Unit Economics',
    impact: 'Defines minimum survival thresholds before expanding overheads.',
  },
  {
    id: 7,
    text: 'Your receivables aging is monitored weekly with clear follow-up actions',
    category: 'Cash Collection',
    impact: 'Accelerates cash turnaround and eliminates bad debts.',
  },
  {
    id: 8,
    text: 'You use financial data proactively to guide pricing and investment decisions',
    category: 'Strategic CFO',
    impact: 'Moves management from gut feelings to numbers-backed bets.',
  },
  {
    id: 9,
    text: 'Your bookkeeping is automated and you rarely touch manual entries',
    category: 'AI Automation',
    impact: 'Saves 20+ hours per month and removes human entry discrepancies.',
  },
  {
    id: 10,
    text: 'Finance is seen as a strategic partner, not just a compliance function',
    category: 'Strategic CFO',
    impact: 'Transforms finance from a tax burden to a growth accelerator.',
  },
  {
    id: 11,
    text: 'You perform automated bank, EDC card swipe, and payment reconciliations regularly',
    category: 'Leakage Control',
    impact: 'Stops invisible settlement leakages in card batches, aggregators, and vendor payouts.',
  },
  {
    id: 12,
    text: 'Your statutory tax clauses (MSME 43B(h), GST 2B ITC, Sec 40A(3)) are monitored continuously',
    category: 'Statutory Shield',
    impact: 'Protects against surprise tax disallowance penalties and reclaims 100% of eligible GST input credit.',
  },
];

export const DiagnosticTool: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const [answers, setAnswers] = useState<Record<number, boolean>>({});
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
  });

  const handleAnswer = (id: number, val: boolean) => {
    const nextAnswers = { ...answers, [id]: val };
    setAnswers(nextAnswers);

    const yesCount = Object.values(nextAnswers).filter(Boolean).length;
    if (yesCount === 12 && Object.keys(nextAnswers).length === 12) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#00E676', '#E53935', '#00B0FF'],
      });
    }
  };

  const answeredCount = Object.keys(answers).length;
  const yesCount = Object.values(answers).filter(Boolean).length;
  const scorePercent = Math.round((yesCount / 12) * 100);

  const getStageInfo = () => {
    if (answeredCount === 0) {
      return {
        stage: 'Diagnostic Ready',
        color: 'var(--text-secondary)',
        desc: 'Answer the 12 questions below to reveal your business financial stage.',
        icon: HelpCircle,
      };
    }
    if (answeredCount < 12) {
      return {
        stage: `In Progress (${12 - answeredCount} remaining)`,
        color: 'var(--brand-red-light)',
        desc: `You have answered ${answeredCount} of 12 questions. Keep going to unlock your full diagnostic.`,
        icon: Zap,
      };
    }
    if (yesCount <= 4) {
      return {
        stage: 'Foundation Stage (0–4 Points)',
        color: '#ff5252',
        desc: 'Significant gaps exist in your financial intelligence and decision-support capabilities. Your business operates predominantly on historical recording rather than proactive steering.',
        icon: ShieldAlert,
      };
    }
    if (yesCount <= 8) {
      return {
        stage: 'Development Stage (5–8 Points)',
        color: 'var(--signal-amber)',
        desc: 'You have some intelligence capabilities, but blindspots remain in cash forecasting, margin leaks, or automation. Strategic improvements will unlock faster, more confident decisions.',
        icon: Award,
      };
    }
    return {
      stage: 'Intelligence Stage (9–12 Points)',
      color: 'var(--signal-green)',
      desc: "You're operating at a high strategic level. Finance is a strategic asset. Minor optimizations with Forty2's AI and CA team will refine your competitive edge further.",
      icon: Sparkles,
    };
  };

  const stage = getStageInfo();

  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLeadSubmitted(true);
  };

  const whatsappMessage = encodeURIComponent(
    `Hi Forty2 Team, I just completed the 12-Point Financial Diagnostic for ${formData.company || 'my company'}.\nMy Score: ${yesCount}/12 (${stage.stage}).\nI'd like to schedule a 20-min Financial Diagnostic consultation.`
  );

  return (
    <div
      className="glass-panel-elevated"
      style={{
        padding: compact ? '24px' : '40px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background spreadsheet grid header homage */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          marginBottom: '28px',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: 'rgba(229, 57, 53, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(229, 57, 53, 0.3)',
            }}
          >
            <span style={{ color: 'var(--brand-red)', fontWeight: 800, fontSize: '1rem' }}>42</span>
          </div>
          <div>
            <span
              className="font-mono"
              style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                display: 'block',
              }}
            >
              FORTY2_Diagnostic.xlsx — Financial Intelligence Assessment
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              12 Point Diagnostic Protocol · MSME & Startup Financial Health
            </span>
          </div>
        </div>

        {/* Live Score Dial */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            background: 'var(--border-subtle)',
            padding: '8px 18px',
            borderRadius: '12px',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', textTransform: 'uppercase' }}>
              CURRENT SCORE
            </span>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
              <span className="font-mono" style={{ fontSize: '1.6rem', fontWeight: 800, color: stage.color }}>
                {yesCount}
              </span>
              <span className="font-mono" style={{ fontSize: '0.95rem', color: 'var(--text-muted)' }}>
                / 12
              </span>
            </div>
          </div>

          <div style={{ width: '80px', height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${scorePercent}%`,
                height: '100%',
                background: stage.color,
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      </div>

      {/* Stage Status Card */}
      <div
        style={{
          padding: '16px 20px',
          borderRadius: '12px',
          backgroundColor: 'var(--border-subtle)',
          border: `1px solid ${stage.color}40`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '14px',
          marginBottom: '32px',
        }}
      >
        <stage.icon size={22} color={stage.color} style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: stage.color, marginBottom: '4px' }}>
            {stage.stage}
          </h4>
          <p style={{ fontSize: '0.86rem', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
            {stage.desc}
          </p>
        </div>
      </div>

      {/* 12 Questions List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '36px' }}>
        {QUESTIONS.map((q) => {
          const isAnswered = answers[q.id] !== undefined;
          const isYes = answers[q.id] === true;
          const isNo = answers[q.id] === false;

          return (
            <div
              key={q.id}
              style={{
                padding: '16px 18px',
                borderRadius: '12px',
                backgroundColor: isAnswered
                  ? isYes
                    ? 'rgba(0, 230, 118, 0.08)'
                    : 'rgba(229, 57, 53, 0.08)'
                  : 'var(--border-subtle)',
                border: isAnswered
                  ? isYes
                    ? '1px solid rgba(0, 230, 118, 0.3)'
                    : '1px solid rgba(229, 57, 53, 0.3)'
                  : '1px solid var(--border-medium)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '14px',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', maxWidth: '750px' }}>
                <span
                  className="font-mono"
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: isAnswered ? (isYes ? 'var(--signal-green)' : 'var(--brand-red)') : 'var(--text-muted)',
                    minWidth: '24px',
                  }}
                >
                  {q.id < 10 ? `0${q.id}` : q.id}
                </span>

                <div>
                  <div style={{ fontSize: '0.94rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {q.text}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{q.category}</span> · {q.impact}
                  </div>
                </div>
              </div>

              {/* Yes / No Toggle Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleAnswer(q.id, true)}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '8px',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    transition: 'all 0.15s ease',
                    backgroundColor: isYes ? 'var(--signal-green)' : 'var(--border-subtle)',
                    color: isYes ? '#ffffff' : 'var(--text-secondary)',
                    border: isYes ? '1px solid var(--signal-green)' : '1px solid var(--border-subtle)',
                    boxShadow: isYes ? '0 0 14px var(--signal-green-glow)' : 'none',
                  }}
                >
                  YES
                </button>

                <button
                  type="button"
                  onClick={() => handleAnswer(q.id, false)}
                  style={{
                    padding: '8px 18px',
                    borderRadius: '8px',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    transition: 'all 0.15s ease',
                    backgroundColor: isNo ? 'var(--brand-red)' : 'var(--border-subtle)',
                    color: isNo ? '#ffffff' : 'var(--text-secondary)',
                    border: isNo ? '1px solid var(--brand-red)' : '1px solid var(--border-subtle)',
                    boxShadow: isNo ? '0 0 14px var(--brand-red-glow)' : 'none',
                  }}
                >
                  NO
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversion & Consultation Section */}
      <div
        style={{
          marginTop: '32px',
          padding: '28px',
          borderRadius: '16px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-medium)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>
              Ready to Close Your Financial Gaps?
            </h3>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', maxWidth: '600px' }}>
              Your diagnostic score shows where you stand ({yesCount}/12). Forty2 helps you reach 12/12 with automated bookkeeping, AI-driven leakage alerts, and Chartered Accountant guidance.
            </p>
          </div>

          <a
            href={`https://wa.me/919789367892?text=${whatsappMessage}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            style={{
              padding: '12px 20px',
              color: 'var(--signal-green)',
              borderColor: 'rgba(5, 150, 105, 0.3)',
            }}
          >
            <MessageCircle size={18} />
            <span>Chat on WhatsApp ({yesCount}/12 Score)</span>
          </a>
        </div>

        {/* Lead Capture Form */}
        {!leadSubmitted ? (
          <form onSubmit={handleLeadSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <input
              type="text"
              required
              placeholder="Your Full Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-medium)',
                color: 'var(--text-primary)',
                fontSize: '0.88rem',
              }}
            />
            <input
              type="email"
              required
              placeholder="Work Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-medium)',
                color: 'var(--text-primary)',
                fontSize: '0.88rem',
              }}
            />
            <input
              type="text"
              placeholder="Company Name"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-medium)',
                color: 'var(--text-primary)',
                fontSize: '0.88rem',
              }}
            />
            <button type="submit" className="btn-primary" style={{ height: '100%', minHeight: '44px' }}>
              <span>Get Full Audit Report</span>
              <Send size={15} />
            </button>
          </form>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--signal-green)', background: 'rgba(0,230,118,0.1)', padding: '14px', borderRadius: '8px' }}>
            <CheckCircle2 size={20} />
            <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>
              Thank you, {formData.name}! Your diagnostic audit request has been sent to our CA partners at ksr@forty2.online. We will be in touch shortly.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
